import dayjs from "dayjs";
import i18next from "i18next";
import { useTranslation } from "react-i18next";

import { ReactComponent as NoMessagesImage } from "../../../../../../assets/images/woman-computer.svg";
import { createColumn, Table } from "../../../../../../components/Table";
import {
  formatHoursAndMinutes,
  formatMinutesToHoursAndMinutes,
} from "../../../../../../utils/dateFormatter";
import { FitbitChartRow } from "../FitbitActivityChart/FitbitActivityChart";
import {
  ImageContainer,
  NoContentMessage,
  TableContainer,
} from "./FitbitActivityTable.styles";

type FitbitActivityTableProps = {
  data: FitbitChartRow[];
  viewMode: "daily" | "monthly";
  isPinnedGraph?: boolean;
};

interface IFitbitTableRow {
  id: number;
  date: string;
  activeHours: string;
  sedentaryHours: string;
  lightlyActive: string;
  fairlyActive: string;
  veryActive: string;
}

export function FitbitActivityTable({
  data,
  viewMode,
  isPinnedGraph,
}: FitbitActivityTableProps) {
  const { t } = useTranslation();

  if (data.length === 0) {
    return (
      <ImageContainer>
        <NoMessagesImage width={181} height={158} />
        <NoContentMessage>
          {t("patientPage_measurementModal_activity_tableEmpty")}
        </NoContentMessage>
      </ImageContainer>
    );
  }

  const tableData: IFitbitTableRow[] = data.map((row, index) => ({
    id: index,
    date: row.isMonthly
      ? row.label ?? dayjs(row.timeValue).format("MMM YYYY")
      : dayjs(row.timeValue).format("MMM D, YYYY"),
    activeHours: formatHoursAndMinutes(row.activeHours),
    sedentaryHours: formatHoursAndMinutes(row.sedentaryHours),
    lightlyActive: formatMinutesToHoursAndMinutes(row.minutesLightlyActive),
    fairlyActive: formatMinutesToHoursAndMinutes(row.minutesFairlyActive),
    veryActive: formatMinutesToHoursAndMinutes(row.minutesVeryActive),
  }));

  const dateHeader =
    viewMode === "monthly"
      ? t("patientPage_columns_month")
      : t("patientPage_columns_date");

  const columns = [
    createColumn({
      keyName: "date",
      header: () => dateHeader,
      cell: (info) => info.renderValue(),
      size: 150,
    }),
    createColumn({
      keyName: "activeHours",
      header: () => i18next.t("patientPage_columns_active"),
      cell: (info) => info.renderValue(),
      size: 100,
    }),
    createColumn({
      keyName: "sedentaryHours",
      header: () => i18next.t("patientPage_columns_sedentary"),
      cell: (info) => info.renderValue(),
      size: 100,
    }),
    createColumn({
      keyName: "lightlyActive",
      header: () => i18next.t("patientPage_columns_lightly_active"),
      cell: (info) => info.renderValue(),
      size: 120,
    }),
    createColumn({
      keyName: "fairlyActive",
      header: () => i18next.t("patientPage_columns_fairly_active"),
      cell: (info) => info.renderValue(),
      size: 120,
    }),
    createColumn({
      keyName: "veryActive",
      header: () => i18next.t("patientPage_columns_very_active"),
      cell: (info) => info.renderValue(),
      size: 120,
    }),
  ];

  return (
    <TableContainer isPinnedGraph={isPinnedGraph}>
      <Table columns={columns} data={tableData} />
    </TableContainer>
  );
}
